import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import JitsiMeet from '../components/JitsiMeet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Clock, User, Check, X, AlertCircle, BarChart3, Video, Building, Settings, Link as LinkIcon, CreditCard, CheckCircle, Key } from 'lucide-react';
import { format, parseISO, isPast, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PractitionerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated, isPractitioner } = useAuth();
  
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
  const [profile, setProfile] = useState(null);
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  // Password change
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    // Check for calendar connection result
    if (searchParams.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connecté avec succès !');
    } else if (searchParams.get('calendar_error') === 'true') {
      toast.error('Erreur lors de la connexion à Google Calendar');
    }
    
    // Check for subscription result
    if (searchParams.get('subscription') === 'success') {
      toast.success('Abonnement activé avec succès !');
      fetchSubscription();
    } else if (searchParams.get('subscription') === 'cancelled') {
      toast.info('Abonnement non finalisé');
    }
  }, [searchParams]);
  
  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    if (!isPractitioner) {
      navigate('/client/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [appointmentsRes, statsRes, profileRes, integrationsRes, subscriptionRes] = await Promise.all([
          axios.get(`${API}/appointments/me`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/stats/practitioner`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/practitioners/me/profile`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
          axios.get(`${API}/integrations/status`).catch(() => ({ data: {} })),
          axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
        ]);
        
        setAppointments(appointmentsRes.data);
        setStats(statsRes.data);
        if (profileRes?.data) setProfile(profileRes.data);
        setIntegrations(integrationsRes.data);
        if (subscriptionRes?.data) setSubscription(subscriptionRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 404) {
          navigate('/practitioner/setup');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, isPractitioner, navigate, token]);

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, status } : apt
      ));
      
      // Update stats
      const statusLabels = { confirmed: 'confirmé', cancelled: 'annulé', completed: 'terminé' };
      toast.success(`Rendez-vous ${statusLabels[status]}`);
      
      setStats(prev => {
        const updated = { ...prev };
        const oldStatus = appointments.find(a => a.id === appointmentId)?.status;
        if (oldStatus) updated[oldStatus]--;
        updated[status]++;
        return updated;
      });
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSubscribe = async () => {
    setLoadingSubscription(true);
    try {
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'abonnement');
      setLoadingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ? Il restera actif jusqu\'à la fin de la période en cours.')) {
      return;
    }
    
    setLoadingSubscription(true);
    try {
      await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abonnement annulé');
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Mot de passe modifié avec succès');
      setPasswordModalOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  // Filter appointments
  const upcomingAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.date);
    return (!isPast(aptDate) || isToday(aptDate)) && apt.status !== 'cancelled';
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  const pastAppointments = appointments.filter(apt => {
    const aptDate = parseISO(apt.date);
    return isPast(aptDate) && !isToday(aptDate);
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge-status badge-pending">En attente</span>;
      case 'confirmed':
        return <span className="badge-status badge-confirmed">Confirmé</span>;
      case 'cancelled':
        return <span className="badge-status badge-cancelled">Annulé</span>;
      case 'completed':
        return <span className="badge-status badge-completed">Terminé</span>;
      default:
        return null;
    }
  };

  // Chart data
  const chartData = [
    { name: 'En attente', value: stats.pending, fill: '#F59E0B' },
    { name: 'Confirmés', value: stats.confirmed, fill: '#10B981' },
    { name: 'Terminés', value: stats.completed, fill: '#3B82F6' },
    { name: 'Annulés', value: stats.cancelled, fill: '#EF4444' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container-app py-20 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-6 text-terracotta-500" />
          <h1 className="font-heading text-2xl font-semibold mb-4">Profil praticien incomplet</h1>
          <p className="text-muted-foreground mb-6">
            Complétez votre profil pour commencer à recevoir des rendez-vous
          </p>
          <Link to="/practitioner/setup">
            <Button className="bg-sage-500 hover:bg-sage-600" data-testid="setup-profile-btn">
              Configurer mon profil
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="practitioner-dashboard">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground mb-2">
              Bonjour, {user?.first_name} !
            </h1>
            <p className="text-muted-foreground">
              Gérez vos rendez-vous et vos disponibilités
            </p>
          </div>
          <Link to="/practitioner/setup">
            <Button variant="outline" className="shrink-0" data-testid="edit-profile-btn">
              <Settings className="w-4 h-4 mr-2" />
              Modifier mon profil
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card" data-testid="stat-total">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total RDV</span>
              <Calendar className="w-5 h-5 text-sage-500" />
            </div>
            <p className="text-2xl font-heading font-semibold">{stats.total}</p>
          </div>
          <div className="stat-card" data-testid="stat-pending">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">En attente</span>
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-2xl font-heading font-semibold text-amber-600">{stats.pending}</p>
          </div>
          <div className="stat-card" data-testid="stat-confirmed">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Confirmés</span>
              <Check className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-heading font-semibold text-green-600">{stats.confirmed}</p>
          </div>
          <div className="stat-card" data-testid="stat-completed">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Terminés</span>
              <BarChart3 className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-heading font-semibold text-blue-600">{stats.completed}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Appointments */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-sand-100 p-1 rounded-xl">
                <TabsTrigger 
                  value="upcoming" 
                  className="rounded-lg data-[state=active]:bg-white"
                  data-testid="tab-upcoming"
                >
                  À venir ({upcomingAppointments.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="past" 
                  className="rounded-lg data-[state=active]:bg-white"
                  data-testid="tab-past"
                >
                  Historique ({pastAppointments.length})
                </TabsTrigger>
              </TabsList>

              {/* Upcoming Appointments */}
              <TabsContent value="upcoming">
                {upcomingAppointments.length === 0 ? (
                  <div className="empty-state bg-white rounded-2xl border border-sand-200">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-heading text-xl font-medium mb-2">Aucun rendez-vous à venir</h3>
                    <p className="text-muted-foreground">
                      Vos prochains rendez-vous apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((apt) => (
                      <div 
                        key={apt.id}
                        className="bg-white rounded-2xl border border-sand-200 shadow-soft p-5"
                        data-testid={`appointment-${apt.id}`}
                      >
                        <div className="flex flex-col gap-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center shrink-0">
                                <User className="w-6 h-6 text-sage-600" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-medium text-foreground">
                                    {apt.client?.first_name} {apt.client?.last_name}
                                  </h3>
                                  {getStatusBadge(apt.status)}
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {format(parseISO(apt.date), 'EEEE d MMMM', { locale: fr })}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {apt.start_time} - {apt.end_time}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    {apt.consultation_type === 'cabinet' ? (
                                      <><Building className="w-4 h-4" /> Cabinet</>
                                    ) : (
                                      <><Video className="w-4 h-4" /> Visio</>
                                    )}
                                  </span>
                                </div>
                                {apt.notes && (
                                  <p className="text-sm text-muted-foreground mt-2 italic">
                                    "{apt.notes}"
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          {apt.status === 'pending' && (
                            <div className="flex gap-2 pt-2 border-t border-sand-100">
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => updateAppointmentStatus(apt.id, 'confirmed')}
                                data-testid={`confirm-${apt.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Confirmer
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => updateAppointmentStatus(apt.id, 'cancelled')}
                                data-testid={`cancel-${apt.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Refuser
                              </Button>
                            </div>
                          )}
                          {apt.status === 'confirmed' && (
                            <div className="flex gap-2 pt-2 border-t border-sand-100">
                              {apt.consultation_type === 'visio' && apt.video_room_url && (
                                <Button 
                                  size="sm"
                                  className="bg-terracotta-500 hover:bg-terracotta-600"
                                  onClick={() => setActiveVideoCall({ url: apt.video_room_url, userName: `Dr. ${user?.first_name || 'Praticien'}` })}
                                  data-testid={`join-video-${apt.id}`}
                                >
                                  <Video className="w-4 h-4 mr-1" />
                                  Rejoindre
                                </Button>
                              )}
                              <Button 
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => updateAppointmentStatus(apt.id, 'completed')}
                                data-testid={`complete-${apt.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Marquer terminé
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => updateAppointmentStatus(apt.id, 'cancelled')}
                                data-testid={`cancel-confirmed-${apt.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Annuler
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Past Appointments */}
              <TabsContent value="past">
                {pastAppointments.length === 0 ? (
                  <div className="empty-state bg-white rounded-2xl border border-sand-200">
                    <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="font-heading text-xl font-medium mb-2">Aucun historique</h3>
                    <p className="text-muted-foreground">
                      Vos consultations passées apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastAppointments.map((apt) => (
                      <div 
                        key={apt.id}
                        className="bg-white rounded-2xl border border-sand-200 shadow-soft p-5 opacity-75"
                        data-testid={`past-appointment-${apt.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-sand-100 rounded-xl flex items-center justify-center shrink-0">
                            <User className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="font-medium text-foreground">
                                {apt.client?.first_name} {apt.client?.last_name}
                              </h3>
                              {getStatusBadge(apt.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {format(parseISO(apt.date), 'EEEE d MMMM yyyy', { locale: fr })} à {apt.start_time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Chart */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6" data-testid="stats-chart">
              <h2 className="font-heading text-lg font-medium mb-6">Statistiques</h2>
              
              {stats.total > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} layout="vertical">
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Aucune donnée disponible</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 mt-6" data-testid="quick-actions">
              <h2 className="font-heading text-lg font-medium mb-4">Actions rapides</h2>
              <div className="space-y-3">
                <Link to="/practitioner/setup" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Settings className="w-4 h-4 mr-2" />
                    Modifier mon profil
                  </Button>
                </Link>
                <Link to={`/practitioner/${profile?.id}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <User className="w-4 h-4 mr-2" />
                    Voir ma page publique
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setPasswordModalOpen(true)}
                  data-testid="change-password-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Changer mon mot de passe
                </Button>
              </div>
            </div>

            {/* Google Calendar Integration */}
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 mt-6" data-testid="calendar-integration">
              <h2 className="font-heading text-lg font-medium mb-4">Google Calendar</h2>
              {user?.google_calendar_connected ? (
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="w-5 h-5" />
                  <span className="text-sm">Connecté - vos RDV sont synchronisés</span>
                </div>
              ) : integrations.google_calendar ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Synchronisez automatiquement vos rendez-vous avec Google Calendar
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/oauth/calendar/login`);
                        window.location.href = response.data.authorization_url;
                      } catch (error) {
                        toast.error('Erreur de connexion');
                      }
                    }}
                    data-testid="connect-google-calendar"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Connecter Google Calendar
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Google Calendar non configuré sur ce serveur
                </p>
              )}
            </div>

            {/* Subscription Section */}
            {subscription && (
              <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 mt-6" data-testid="subscription-section">
                <h2 className="font-heading text-lg font-medium mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-sage-500" />
                  Abonnement mensuel
                </h2>
                
                {subscription.subscription_status === 'active' ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Abonnement actif</span>
                    </div>
                    
                    {subscription.stripe_subscription && (
                      <div className="text-sm text-muted-foreground">
                        <p>Prochaine facturation : {new Date(subscription.stripe_subscription.current_period_end).toLocaleDateString('fr-FR')}</p>
                        {subscription.stripe_subscription.cancel_at_period_end && (
                          <p className="text-orange-600 mt-1">⚠️ Annulation prévue à la fin de la période</p>
                        )}
                      </div>
                    )}
                    
                    {!subscription.stripe_subscription?.cancel_at_period_end && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleCancelSubscription}
                        disabled={loadingSubscription}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {loadingSubscription ? 'Annulation...' : 'Annuler l\'abonnement'}
                      </Button>
                    )}
                  </div>
                ) : subscription.subscription_status === 'cancelling' ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-orange-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Abonnement en cours d'annulation</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Votre abonnement restera actif jusqu'à la fin de la période en cours.
                    </p>
                  </div>
                ) : subscription.subscription_status === 'past_due' ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-medium">Paiement en retard</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Veuillez mettre à jour votre moyen de paiement.
                    </p>
                  </div>
                ) : subscription.subscription_enabled === false ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Cotisation mensuelle de <span className="font-semibold text-foreground">{subscription.price}€/mois</span> pour accéder à toutes les fonctionnalités.
                    </p>
                    
                    <div className="p-3 bg-sage-50 rounded-xl text-sm">
                      <p className="font-medium text-sage-700 mb-2">Inclus dans l'abonnement :</p>
                      <ul className="space-y-1 text-sage-600">
                        <li>✓ Profil visible sur la plateforme</li>
                        <li>✓ Gestion des rendez-vous</li>
                        <li>✓ Visioconférences illimitées</li>
                        <li>✓ Notifications email</li>
                      </ul>
                    </div>
                    
                    <Button 
                      onClick={handleSubscribe}
                      disabled={loadingSubscription}
                      className="w-full btn-primary"
                      data-testid="subscribe-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {loadingSubscription ? 'Chargement...' : `S'abonner - ${subscription.price}€/mois`}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Votre abonnement n'est pas encore configuré.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Jitsi Video Call Modal */}
      {activeVideoCall && (
        <JitsiMeet
          roomUrl={activeVideoCall.url}
          userName={activeVideoCall.userName}
          onClose={() => setActiveVideoCall(null)}
        />
      )}

      {/* Change Password Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-sage-600" />
              Changer mon mot de passe
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="rounded-xl border-sand-200"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Minimum 6 caractères"
                className="rounded-xl border-sand-200"
                required
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="rounded-xl border-sand-200"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setPasswordModalOpen(false)}
                className="rounded-xl"
              >
                Annuler
              </Button>
              <Button 
                type="submit"
                disabled={changingPassword}
                className="bg-sage-500 hover:bg-sage-600 rounded-xl"
                data-testid="submit-change-password"
              >
                {changingPassword ? 'Modification...' : 'Modifier'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PractitionerDashboard;
